import { useState, useCallback } from 'react';
import { message } from '@revfluence/fresh';
import { PostInsights } from '@frontend/gql/social/graphql';
import { useImageDownloader } from '@frontend/hooks';
import { useGenericTable, UseGenericTableConfig } from './useGenericTable';

type PostTableRow = Omit<PostInsights, 'memberId' | 'accountEntityId'>;

export const usePostsTable = (config: UseGenericTableConfig<PostTableRow>) => {
  const { tableProps } = useGenericTable<PostTableRow>(config);
  const { onFilteredRowsChange } = config;
  const [selectedRows, setSelectedRows] = useState<PostTableRow[]>([]);
  const [filteredCount, setFilteredCount] = useState<number>(config.data.length);
  const [filteredData, setFilteredData] = useState<PostTableRow[]>(config.data);

  const {
    downloadImages,
    isDownloading,
    progress
  } = useImageDownloader({
    onProgress: (progress: number) => {
      message.info(`Download progress: ${Math.round(progress * 100)}%`);
    }
  });

  const handleFilteredRowsChange = useCallback((count: number, data?: PostTableRow[]) => {
    setFilteredCount(count);
    if (data) {
      setFilteredData(data);
    }
    onFilteredRowsChange?.(count);
  }, [onFilteredRowsChange]);

  const onSelectionChange = useCallback((rows: PostTableRow[]) => {
    setSelectedRows((prevRows) => {
      // Only update if the rows are actually different
      if (rows.length !== prevRows.length ||
          rows.some((row, index) => row !== prevRows[index])) {
        return rows;
      }
      return prevRows;
    });
  }, []);

  const handleDownloadPosts = useCallback(async () => {
    if (!selectedRows.length) {
      message.warning('Please select posts to download');
      return;
    }

    try {
      // Generate filename based on selection
      const timestamp = new Date().toISOString();
      const filename = `social_posts_${timestamp}`;

      const downloadItems = selectedRows
        .filter((post): post is PostTableRow & { entityId: string; postPermalink: string } =>
          !!post.entityId && !!post.postPermalink
        )
        .map((post) => ({
          entityId: post.entityId,
          postPermalink: post.postPermalink,
        }));

      if (!downloadItems.length) {
        message.warning('No valid posts found to download');
        return;
      }

      const result = await downloadImages(downloadItems, filename);

      // Provide feedback about partial success if needed
      if (result.failed.length > 0) {
        message.warning(
          `${result.successful.length} images downloaded successfully, ${result.failed.length} failed`
        );
      }
    } catch (error) {
      message.error(`Failed to download images: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [selectedRows, downloadImages]);

  return {
    postTableProps: {
      ...tableProps,
      onSelectionChange,
      onFilteredRowsChange: handleFilteredRowsChange,
      selectedRows,
    },
    postTableActions: {
      onDownloadPosts: handleDownloadPosts,
      isDownloading,
      downloadProgress: progress,
    },
    filteredCount,
    filteredData,
  };
};
